import { Box, Wrap, WrapItem } from '@chakra-ui/react';
import { VisuallyHidden } from '@chakra-ui/visually-hidden';
import { ChangeEvent, ReactNode, useCallback } from 'react';
import { colors } from 'styles/colors';

export type ChipGroupOption<T extends string = string> = {
  value: T;
  label: ReactNode;
};

export type ChipGroupProps<T extends string = string> = {
  /** Shared `name` for radios (one selection per group). */
  name: string;
  options: readonly ChipGroupOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  /** Renders as `<legend>`; preferred accessible name for the group. */
  groupLabel?: ReactNode;
  /** Used when `groupLabel` is omitted (`aria-label` on the fieldset). */
  'aria-label'?: string;
  isDisabled?: boolean;
  /** `soft` = lavender selected; `solid` = dark purple fill and white text (e.g. category chips). */
  selectedVisual?: 'soft' | 'solid';
};

/**
 * Single-select chips using **native `radio` inputs** (shared `name`) for accessibility:
 * **Tab** moves through each option in DOM order; **Space** activates the focused radio;
 * **Arrow keys** move between radios in the same named group in supporting browsers.
 */
export function ChipGroup<T extends string>({
  name,
  options,
  value,
  onChange,
  groupLabel,
  'aria-label': ariaLabel,
  isDisabled = false,
  selectedVisual = 'soft',
}: ChipGroupProps<T>) {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value as T);
    },
    [onChange],
  );

  return (
    <Box
      as='fieldset'
      border='none'
      p={0}
      m={0}
      minW={0}
      w='full'
      aria-label={groupLabel == null ? ariaLabel : undefined}
    >
      {groupLabel != null ? (
        <Box
          as='legend'
          display='block'
          w='full'
          fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
          fontWeight='600'
          fontSize='md'
          lineHeight={1.4}
          color='dark.D800'
          mb={2}
          float='none'
        >
          {groupLabel}
        </Box>
      ) : null}
      <Wrap spacing={2} rowGap={2} shouldWrapChildren={false}>
        {options.map((opt) => {
          const isChecked = value === opt.value;
          const inputId = `${name}-${opt.value}`;
          const isSolid = selectedVisual === 'solid' && isChecked;
          const isSoft = selectedVisual === 'soft' && isChecked;

          return (
            <WrapItem key={opt.value}>
              <Box
                as='label'
                htmlFor={inputId}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                opacity={isDisabled ? 0.5 : 1}
                userSelect='none'
                borderWidth='1px'
                borderStyle='solid'
                borderRadius='xl'
                borderColor={isChecked ? colors.b2b.chipSelected : colors.b2b.chipInactiveBorder}
                px={3}
                py={2}
                bg={
                  isSolid
                    ? colors.b2b.chipSelected
                    : isSoft
                    ? colors.b2b.landingFeatureIconBg
                    : 'white'
                }
                color={isSolid ? 'white' : isSoft ? colors.b2b.chipSelected : 'dark.D800'}
                textStyle='bodySmallMedium'
                transition='background 0.15s ease, border-color 0.15s ease, color 0.15s ease'
                _hover={
                  isDisabled
                    ? undefined
                    : {
                        borderColor: isSolid
                          ? colors.b2b.chipSelectedHover
                          : isChecked
                          ? colors.b2b.chipSelectedHover
                          : 'dark.D200',
                        bg: isSolid ? colors.b2b.chipSelectedHover : isSoft ? '#E4DDF2' : 'dark.D0',
                        color: isSolid
                          ? 'white'
                          : isChecked
                          ? colors.b2b.chipSelectedHover
                          : 'dark.D800',
                      }
                }
                _focusWithin={
                  isDisabled
                    ? undefined
                    : {
                        borderColor: colors.b2b.chipSelected,
                        boxShadow: `0 0 0 1px ${colors.b2b.chipSelected}`,
                      }
                }
              >
                <VisuallyHidden>
                  <input
                    id={inputId}
                    type='radio'
                    name={name}
                    value={opt.value}
                    checked={isChecked}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </VisuallyHidden>
                <Box as='span'>{opt.label}</Box>
              </Box>
            </WrapItem>
          );
        })}
      </Wrap>
    </Box>
  );
}
